import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { useRouter } from 'next/router'
import { useRecoilValue, useRecoilState } from 'recoil'
import * as jose from 'jose'
import dayjs from 'dayjs'
import doctorAtom, { accessTokenState } from '../store/DoctorAtom'
import { get } from '../api/auth'

interface AuthContextType {
  isAuthenticated: boolean
  loading: boolean
  doctor: any
  login: (token: string) => Promise<void>
  logout: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const doctor = useRecoilValue(doctorAtom)
  const [accessToken, setAccessToken] = useRecoilState(accessTokenState)
  const router = useRouter()

  useEffect(() => {
    const checkAuth = async () => {
      try {
        if (accessToken) {
          const exp = jose.decodeJwt(accessToken).exp
          if (exp && dayjs(exp * 1000).isAfter(dayjs().add(1, 'day'))) {
            setIsAuthenticated(true)
          } else {
            setAccessToken(null)
            setIsAuthenticated(false)
          }
        } else {
          setIsAuthenticated(false)
        }
      } catch (error) {
        setAccessToken(null)
        setIsAuthenticated(false)
      } finally {
        setLoading(false)
      }
    }

    checkAuth()
  }, [accessToken, setAccessToken])

  const login = async (token: string) => {
    try {
      setAccessToken(token)
      await get({ accessToken: token })
      setIsAuthenticated(true)
    } catch (error) {
      setAccessToken(null)
      setIsAuthenticated(false)
      throw error
    }
  }

  const logout = () => {
    setAccessToken(null)
    setIsAuthenticated(false)
    router.push('/signin')
  }

  const value = {
    isAuthenticated,
    loading,
    doctor,
    login,
    logout
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
} 